import React, { useEffect, useState } from "react";
import { SelectCustom } from "../../../components/ui/SelectCustom";

import { ReactComponent as ChevronRightIcon } from "../../../assets/img/img-09-10-23/arrow-right.svg";
import { useDispatch, useSelector } from "react-redux";

import { statesOptions } from "../../../data/constants";
import { Controller, useForm } from "react-hook-form";
import { setBillingData } from "../../../redux/slices/coursesSlice";
import { inferCountyFromPostcode } from "../../../helpers/index";

export const BillingDetails = ({ prevHandler, nextHandler }) => {
  const billingData = useSelector((state) => state.courses.billingData);
  const [country, setCountry] = useState(billingData?.country || "");
  const dispatch = useDispatch();
  const bookingType = useSelector((state) => state.courses.bookingType);

  const countryOptions = [
    { label: "United Kingdom", value: "GB" },
    { label: "Ireland", value: "IE" },
    { label: "Australia", value: "AU" },
    { label: "Austria", value: "AT" },
    { label: "Belgium", value: "BE" },
    { label: "Bulgaria", value: "BG" },
    { label: "Canada", value: "CA" },
    { label: "Croatia", value: "HR" },
    { label: "Cyprus", value: "CY" },
    { label: "Czechia", value: "CZ" },
    { label: "Denmark", value: "DK" },
    { label: "Estonia", value: "EE" },
    { label: "Finland", value: "FI" },
    { label: "France", value: "FR" },
    { label: "Germany", value: "DE" },
    { label: "Greece", value: "GR" },
    { label: "Hong Kong", value: "HK" },
    { label: "Hungary", value: "HU" },
    { label: "India", value: "IN" },
    { label: "Italy", value: "IT" },
    { label: "Japan", value: "JP" },
    { label: "Latvia", value: "LV" },
    { label: "Lithuania", value: "LT" },
    { label: "Luxembourg", value: "LU" },
    { label: "Malta", value: "MT" },
    { label: "Netherlands", value: "NL" },
    { label: "New Zealand", value: "NZ" },
    { label: "Norway", value: "NO" },
    { label: "Poland", value: "PL" },
    { label: "Portugal", value: "PT" },
    { label: "Qatar", value: "QA" },
    { label: "Romania", value: "RO" },
    { label: "Saudi Arabia", value: "SA" },
    { label: "Singapore", value: "SG" },
    { label: "Slovakia", value: "SK" },
    { label: "Slovenia", value: "SI" },
    { label: "South Africa", value: "ZA" },
    { label: "Spain", value: "ES" },
    { label: "Sweden", value: "SE" },
    { label: "Switzerland", value: "CH" },
    { label: "United Arab Emirates", value: "AE" },
    { label: "United States", value: "US" },
  ];

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    resetField,
    watch,
    setValue,
    getValues,
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      country: billingData?.country || countryOptions[0].value,
      county: billingData?.county || "",
    },
  });

  // Auto-fill the county from the postcode (GB only), debounced. Watch-based so it
  // fires on input change (independent of focus/blur). Fails open: any error or
  // unmatched postcode just leaves the field editable -- it never blocks checkout.
  const isFieldValid = (n) =>
    !!(touchedFields && touchedFields[n] && !errors?.[n] && watch(n));

  const postalCodeValue = watch("postalCode");
  useEffect(() => {
    if ((country || "GB") !== "GB") return;
    const pc = (postalCodeValue || "").trim();
    if (pc.length < 5) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (getValues("county")) return;
      const match = await inferCountyFromPostcode(
        pc,
        statesOptions[country || "GB"]
      );
      if (!cancelled && match) setValue("county", match, { shouldValidate: true });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [postalCodeValue, country]);

  const submitHandler = (data) => {
    dispatch(setBillingData(data));
    nextHandler();
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <h3 className="mb-24">
        {bookingType !== "single"
          ? "Billing Address Details"
          : "Billing Details"}
      </h3>
      <div className="row gutters-20 mb-30">
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">
              Billing Country<span className="color-danger ms-1">*</span>
            </label>
            <Controller
              name="country"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, ref } }) => (
                <SelectCustom
                  options={countryOptions}
                  onBlur={onBlur}
                  onSelect={(selectedOption) => {
                    onChange(selectedOption);
                    setCountry(selectedOption);
                    resetField("county");
                  }}
                  ref={ref}
                />
              )}
            />
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">
              {["GB", "IE"].includes(country || "GB")
                ? "Billing County"
                : "Billing County / State / Region"}
              {["GB", "IE"].includes(country || "GB") && (
                <span className="color-danger ms-1">*</span>
              )}
            </label>
            <Controller
              name="county"
              control={control}
              rules={{ required: ["GB", "IE"].includes(country || "GB") }}
              render={({ field: { onChange, onBlur, ref, value } }) =>
                ["GB", "IE"].includes(country || "GB") ? (
                  <SelectCustom
                    options={statesOptions[country || "GB"].map((itemOption) => {
                      return { value: itemOption, label: itemOption };
                    })}
                    onBlur={onBlur}
                    onSelect={(selectedOption) => {
                      onChange(selectedOption);
                    }}
                    value={value || ""}
                    placeholder="Start typing your county…"
                    defaultFirst={false}
                    ref={ref}
                  />
                ) : (
                  <input
                    type="text"
                    className="input"
                    placeholder="State / region (optional)"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    ref={ref}
                  />
                )
              }
            />
            {errors.county && (
              <span className="color-danger">
                Please select your billing county
              </span>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">
              Billing City<span className="color-danger ms-1">*</span>
            </label>
            <input
              type="text"
              className={`input ${errors.city ? "error" : isFieldValid("city") ? "is-valid" : ""}`}
              autoComplete="address-level2"
              {...register("city", {
                required: true,
                validate: (value) => {
                  return !!value.trim();
                },
              })}
              placeholder="e.g. London"
              defaultValue={billingData?.city}
            />
            {errors.city && (
              <span className="color-danger">
                {errors.city.message || "Enter Billing City please"}
              </span>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">
              Billing Street<span className="color-danger ms-1">*</span>
            </label>
            <input
              type="text"
              className={`input ${errors.street ? "error" : isFieldValid("street") ? "is-valid" : ""}`}
              autoComplete="address-line1"
              {...register("street", {
                required: true,
                validate: (value) => {
                  return !!value.trim();
                },
              })}
              placeholder="e.g. 31 North Street"
              defaultValue={billingData?.street}
            />
            {errors.street && (
              <span className="color-danger">
                {errors.street.message || "Enter Billing Street please"}
              </span>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">
              Billing Postal Code<span className="color-danger ms-1">*</span>
            </label>
            <input
              type="text"
              className={`input ${errors.postalCode ? "error" : isFieldValid("postalCode") ? "is-valid" : ""}`}
              autoComplete="postal-code"
              {...register("postalCode", {
                required: true,
                validate: (value) => {
                  return !!value.trim();
                },
              })}
              placeholder="e.g. SE93 2GP"
              defaultValue={billingData?.postalCode}
            />
            {errors.postalCode && (
              <span className="color-danger">
                {errors.postalCode.message ||
                  "Enter Billing Postal Code please"}
              </span>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">Company Name</label>
            <input
              type="text"
              className={`input ${errors.companyName ? "error" : ""}`}
              autoComplete="organization"
              {...register("companyName")}
              defaultValue={billingData?.companyName}
            />
          </div>
        </div>
        <div className="col-12">
          <div className="form-group d-flex flex-column">
            <label className="form-group-title">
              Please state any special requirements or comments here:
            </label>
            <textarea
              className={`textarea resize-none ${
                errors.comments ? "error" : ""
              }`}
              {...register("comments")}
              rows="5"
              defaultValue={billingData?.comments}
            />
          </div>
        </div>
      </div>
      <div className="page-action d-flex align-items-center justify-content-between">
        <button
          type="button"
          onClick={prevHandler}
          className="btn btn--outline-secondary btn--md radius-5 ttu min-w-120"
        >
          Back
        </button>
        <button
          className="btn btn--success btn--md radius-5 ttu min-w-240"
          type="submit"
        >
          Continue
          <span className="btn-icon ml-10">
            <ChevronRightIcon />
          </span>
        </button>
      </div>
    </form>
  );
};
